/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { evaluate } from 'mathjs';
export interface FormulaVariables {
  [key: string]: string | number | undefined;
}

export const pattern = /\{(.*?)\}/g; // {property}

export const matchCount = (formula: string, variables: FormulaVariables) => {
  let available = 0;
  formula.replace(pattern, (match, token) => {
    if (variables[token] !== undefined) {
      available++;
    }
    return match;
  });
  return available;
};

export const createExpression = (formula: string, variables: FormulaVariables, defaultVariable?: number) => {
  return formula.replace(pattern, (match, token) => {
    const variable = variables[token];

    if (variable !== undefined) {
      return variable.toString();
    }

    if (defaultVariable !== undefined) {
      return defaultVariable.toString();
    }

    throw new Error(`Unable to replace formula variable ${token} for formula "${formula}"`);
  });
};

export const extractVariables = (formula: string): string[] => {
  const variables = formula.match(pattern);
  return variables ? Array.from(variables.map((variable) => variable.slice(1, variable.length - 1))) : [];
};

type MathScope = object;

const defaultMathScope: MathScope = {
  resolveString: (testVariable: string, testValue: string, onSuccess: number = 1, onFail: number = 0) => {
    if (testVariable === testValue) {
      return onSuccess;
    }
    return onFail;
  },
};

const calculateFormula = ({
  formula,
  variables,
  defaultVariable = 0,
  mathScope = defaultMathScope,
}: {
  formula: string;
  variables: FormulaVariables;
  defaultVariable?: number;
  mathScope?: MathScope;
}) => {
  if (!formula) {
    throw new Error('Formula is not defined for config');
  }

  // Allow to fallback variables to defaultValue
  const replaced = matchCount(formula, variables);
  const variableFallback = replaced > 0 ? defaultVariable : undefined;

  const expression = createExpression(formula, variables, variableFallback);
  return evaluate(expression, mathScope);
};

export const tryCalculation = ({
  formula,
  variables,
  fallback,
  mathScope,
  infinityFallback = 0,
}: {
  formula: string;
  variables: FormulaVariables;
  fallback: number | string;
  mathScope?: MathScope;
  infinityFallback?: number | string | null;
}) => {
  try {
    const value = calculateFormula({ formula, variables, mathScope });
    if (!isFinite(value)) {
      return infinityFallback;
    }
    return value;
  } catch {
    return fallback;
  }
};

export const formatCalculationNumber = (originalNumber: any, decimal?: number): string => {
  if (isNaN(originalNumber) || originalNumber === Infinity || originalNumber === '') {
    return '';
  }

  const number = parseFloat(originalNumber);

  if (typeof decimal === 'number') {
    return number.toLocaleString('en-GB', {
      maximumFractionDigits: decimal,
      minimumFractionDigits: decimal,
    });
  }

  if (number < 1 && number > -1) {
    return number.toLocaleString('en-GB', {
      maximumSignificantDigits: 2,
      minimumSignificantDigits: 1,
    });
  }

  return number.toLocaleString('en-GB', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
};

export const compareMathScope: MathScope = {
  compare: (left: number, operator: string, right: number) => {
    switch (operator) {
      case 'eq': {
        return left === right;
      }
      case 'gt': {
        return left > right;
      }
      default:
        return false;
    }
  },
};
